// Self-hosted live updates for the iOS app (free Capgo plugin, no Capgo Cloud).
//
// deploy.sh publishes the web build as a zip plus a manifest on our own server:
//   https://lax.gomaxdrive.com/app-updates/latest.json  →  { version, url, checksum }
// The app checks the manifest on launch and whenever it comes back to the foreground.
// A newer version is downloaded in the background and applied the next time the app
// goes to the background (or is relaunched), so it never reloads mid-use.
// A bundle that fails to boot is rolled back by the plugin (see notifyAppReady in
// index.js) and is never retried.
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

// REACT_APP_LIVE_UPDATE_URL overrides it for local testing only.
const MANIFEST_URL = process.env.REACT_APP_LIVE_UPDATE_URL || "https://lax.gomaxdrive.com/app-updates/latest.json";
const MIN_CHECK_GAP_MS = 5 * 60 * 1000;

let lastCheck = 0;
let checking = false;

// Numeric dotted-version compare: 1 if a > b, -1 if a < b, 0 if equal.
export function compareVersions(a, b) {
  const pa = String(a).split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d > 0 ? 1 : -1;
  }
  return 0;
}

async function checkForUpdate() {
  if (checking || Date.now() - lastCheck < MIN_CHECK_GAP_MS) return;
  checking = true;
  lastCheck = Date.now();
  try {
    // Native HTTP: the app's origin is capacitor://localhost, so a browser fetch would hit CORS.
    const res = await CapacitorHttp.get({
      url: `${MANIFEST_URL}?t=${Date.now()}`,
      headers: { "Cache-Control": "no-cache" },
      responseType: "json",
    });
    if (res.status !== 200) return;
    const manifest = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
    if (!manifest?.version || !manifest?.url) return;

    const { bundle, native } = await CapacitorUpdater.current();
    const running = bundle?.version && bundle.version !== "builtin" ? bundle.version : native;
    if (compareVersions(manifest.version, running) <= 0) return;

    const { bundles = [] } = await CapacitorUpdater.list();
    const existing = bundles.find(b => b.version === manifest.version);
    if (existing?.status === "error") return; // failed to boot before — don't retry

    const target = existing && existing.status !== "downloading"
      ? existing
      : await CapacitorUpdater.download({
          url: manifest.url,
          version: manifest.version,
          ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
        });
    await CapacitorUpdater.next({ id: target.id });
  } catch (e) {
    console.warn("Live update check failed:", e);
  } finally {
    checking = false;
  }
}

export function startLiveUpdates() {
  if (!Capacitor.isNativePlatform()) return;
  checkForUpdate();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForUpdate();
  });
}
